import { useEffect, useState, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageTitle } from '../../../../../_metronic/layout/core';
import {
  Search,
  Filter,
  Grid3X3,
  List,
  CalendarDays,
  User,
  Eye,
  FileSpreadsheet,
  RefreshCw,
  Tag,
  TrendingUp,
  Gift,
  ListChecks,
  BadgePercent,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  CircleDollarSign,
} from 'lucide-react';

import type { HistoryRecord, HistoryStatus } from '../data/mockHistory';
import { STATUS_LABELS } from '../data/mockHistory';
import { useTemplates } from '../../promotions/data/templates';
import type { FormValues, TemplateDef } from '../../promotions/types';
import {
  exportRebaixaSimples,
  exportRebaixaProgressiva,
  exportCompreEGanhe,
  exportListaSugeridaOriginal,
  exportListaSugeridaRecompra,
  exportCupomSimples,
  exportCupomProgressivo,
  exportCashback,
  exportRebaixaCupomGeral,
  exportBriefingsByTemplate,
} from '../../shared/utils/xlsxExport';
import { fetchBriefingPromocao, fetchBriefingPromocoes } from '../../promotions/services/briefingPromocoesApi';
import HistoryDetailDrawer from '../components/HistoryDetailDrawer';
import {
  getSellerOptionsFromTemplate,
  normalizeSellerFieldsInValues,
} from '../../shared/utils/sellerValues';

type ViewMode = 'grid' | 'list';

const PAGE_SIZE_LIST = 10;
const PAGE_SIZE_GRID = 12;
const CREATE_PROMOTION_ROUTE = '/briefing-promocoes/promocoes';

const XLSX_EXPORTERS: Record<string, (values: FormValues) => void> = {
  t1: exportRebaixaSimples,
  t2: exportRebaixaProgressiva,
  t3: exportCompreEGanhe,
  t4a: exportListaSugeridaOriginal,
  t4b: exportListaSugeridaRecompra,
  t5: exportCupomSimples,
  t6: exportCupomProgressivo,
  t7: exportCashback,
  t8: exportRebaixaCupomGeral,
};

function hasXlsxExporter(record: HistoryRecord) {
  return record.templateId in XLSX_EXPORTERS;
}

function getNormalizedRecordValues(record: HistoryRecord, templateList: TemplateDef[]): FormValues {
  const template = templateList.find((item) => item.id === record.templateId) ?? null;
  return normalizeSellerFieldsInValues(record.values as FormValues, getSellerOptionsFromTemplate(template));
}

function exportRecordXlsx(record: HistoryRecord, templateList: TemplateDef[]) {
  const exporter = XLSX_EXPORTERS[record.templateId];
  if (exporter) exporter(getNormalizedRecordValues(record, templateList));
}

function cloneFormValues(values: FormValues): FormValues {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
  ) as FormValues;
}

function buildReuseState(record: HistoryRecord, templateList: TemplateDef[]) {
  return {
    reuseBriefing: true,
    sourceBriefingId: record.id,
    templateId: record.templateId,
    values: cloneFormValues(getNormalizedRecordValues(record, templateList)),
  };
}

const TEMPLATE_ICONS: Record<string, ReactNode> = {
  t1: <Tag size={16} />,
  t2: <TrendingUp size={16} />,
  t3: <Gift size={16} />,
  t4a: <ListChecks size={16} />,
  t4b: <ListChecks size={16} />,
  t5: <BadgePercent size={16} />,
  t6: <BadgePercent size={16} />,
  t7: <CircleDollarSign size={16} />,
  t8: <Tag size={16} />,
};

const STATUS_FILTER_OPTIONS: { value: HistoryStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'agendado', label: 'Agendado' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'encerrado', label: 'Encerrado' },
];

const STATUS_BADGE_CLASSES: Record<HistoryStatus, string> = {
  ativo: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  agendado: 'bg-sky-100 text-sky-700 ring-sky-200',
  rascunho: 'bg-amber-100 text-amber-700 ring-amber-200',
  encerrado: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const STATUS_CHIP_CLASSES: Record<HistoryStatus, string> = {
  ativo: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  agendado: 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100',
  rascunho: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
  encerrado: 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100',
};

function formatDateRelative(iso: string) {
  if (!iso) return '—';

  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 86400000);

  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Ontem';
  if (diff < 7) return `${diff} dias atrás`;

  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCurrency(value: string) {
  if (!value) return '—';

  return `R$ ${Number(value).toLocaleString('pt-BR')}`;
}

function getPaginationItems(currentPage: number, pageCount: number): Array<number | 'ellipsis'> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis', pageCount];
  }

  if (currentPage >= pageCount - 3) {
    return [1, 'ellipsis', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
  }

  return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', pageCount];
}

function StatusBadge({ status }: { status: HistoryStatus }) {
  return (
    <span
      className={`
        inline-flex h-5 items-center rounded px-2 text-[0.68rem] font-bold leading-none ring-1
        ${STATUS_BADGE_CLASSES[status]}
      `}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

interface HistoryCardProps {
  record: HistoryRecord;
  isSelected: boolean;
  selectionDisabled: boolean;
  onClick: () => void;
  onExportXlsx: () => void;
  onReuse: () => void;
  onToggleSelect: () => void;
}

function HistoryCard({
  record,
  isSelected,
  selectionDisabled,
  onClick,
  onExportXlsx,
  onReuse,
  onToggleSelect,
}: HistoryCardProps) {
  const nomePromo = (record.values.nomePromo as string) || '(sem nome)';
  const nomeExibicao = (record.values.nomeExibicao as string) || '';
  const inicio = (record.values.inicioVigencia as string) || '';
  const fim = (record.values.fimVigencia as string) || '';
  const budget = (record.values.budgetValor as string) || '';

  const canExportXlsx = hasXlsxExporter(record);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`
        group h-full w-full cursor-pointer overflow-hidden rounded-2xl border bg-white text-left
        shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md
        focus:outline-none focus:ring-2 focus:ring-blue-500/30
        ${isSelected ? 'border-blue-400 ring-2 ring-blue-500/20' : 'border-slate-200'}
      `}
      style={{ '--template-color': record.templateColor } as React.CSSProperties}
    >
      
      <div className="h-1 w-full bg-[var(--template-color)]" />

      <div className="p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <label
              title={selectionDisabled ? 'Disponível apenas para o mesmo template selecionado' : 'Selecionar para exportar em lote'}
              aria-label="Selecionar briefing"
              onClick={(e) => e.stopPropagation()}
              className={`
                flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors
                ${isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : selectionDisabled
                    ? 'border-slate-200 bg-slate-50 opacity-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }
              `}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={selectionDisabled}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect();
                }}
                onKeyDown={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
            </label>

            <div
              className="
                flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                bg-[color:var(--template-color)]/10 text-[var(--template-color)]
              "
              style={{
                backgroundColor: `${record.templateColor}18`,
                color: record.templateColor,
              }}
            >
              {TEMPLATE_ICONS[record.templateId]}
            </div>

            <div className="min-w-0">
              <span
                className="
                  block truncate text-[0.65rem] font-bold uppercase leading-none tracking-[0.3px]
                "
                style={{ color: record.templateColor }}
              >
                {record.templateName}
              </span>

              <h3 className="mt-1 truncate text-[0.85rem] font-bold leading-tight text-slate-900">
                {nomePromo}
              </h3>
            </div>
          </div>

          <StatusBadge status={record.status} />
        </div>

        {nomeExibicao && (
          <p className="mb-3 line-clamp-2 text-xs leading-relaxed text-slate-500">
            {nomeExibicao}
          </p>
        )}

        <div className="my-3 h-px bg-slate-100" />

        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {inicio && (
            <div className="flex items-center gap-1.5 text-[0.7rem] text-slate-500">
              <CalendarDays size={12} className="text-slate-400" />
              <span>
                {inicio.slice(0, 10)} → {fim ? fim.slice(0, 10) : '—'}
              </span>
            </div>
          )}

          {budget && (
            <span className="text-[0.7rem] text-slate-500">
              Budget: {formatCurrency(budget)}
            </span>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1.5 text-[0.7rem] text-slate-500">
            <User size={12} className="shrink-0 text-slate-400" />
            <span className="truncate">{record.author}</span>
          </div>

          <span className="shrink-0 text-[0.68rem] text-slate-400">
            {formatDateRelative(record.updatedAt)}
          </span>
        </div>

        <div className="mt-3 flex items-center justify-end gap-1 border-t border-slate-100 pt-3">
          <button
            type="button"
            title="Reaproveitar"
            aria-label="Reaproveitar"
            onClick={(e) => {
              e.stopPropagation();
              onReuse();
            }}
            className="
              flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-blue-700
              transition-colors hover:bg-blue-50
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <RefreshCw size={16} />
            Reaproveitar
          </button>

          {canExportXlsx && (
            <button
              type="button"
              title="Baixar XLSX"
              aria-label="Baixar XLSX"
              onClick={(e) => {
                e.stopPropagation();
                onExportXlsx();
              }}
              className="
                flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-emerald-700
                transition-colors hover:bg-emerald-50
                focus:outline-none focus:ring-2 focus:ring-emerald-500/30
              "
            >
              <FileSpreadsheet size={16} />
              
            </button>
          )}

          <button
            type="button"
            title="Visualizar"
            aria-label="Visualizar"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="
              flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-semibold text-slate-500
              transition-colors hover:bg-slate-100 hover:text-slate-800
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <Eye size={16} />
            Visualizar
          </button>
        </div>
      </div>
    </div>
  );
}

interface HistoryListRowProps {
  record: HistoryRecord;
  isSelected: boolean;
  selectionDisabled: boolean;
  onClick: () => void;
  onExportXlsx: () => void;
  onReuse: () => void;
  onToggleSelect: () => void;
}

function HistoryListHeader() {
  const labelClass = 'text-[0.68rem] font-bold uppercase tracking-[0.4px] text-slate-500';

  return (
    <div
      className="
        hidden items-stretch rounded-xl border border-slate-200 bg-slate-50 shadow-sm sm:flex
      "
    >
      <div className={`flex w-[52px] shrink-0 items-center pl-3 ${labelClass}`}>
        Sel.
      </div>

      <div className="w-1 shrink-0" />

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4 px-4 py-2.5">
        <div className={`min-w-[200px] flex-[2] ${labelClass}`}>
          Promoção
        </div>

        <div className={`hidden min-w-[140px] sm:block ${labelClass}`}>
          Template
        </div>

        <div className={`hidden min-w-[160px] md:block ${labelClass}`}>
          Vigência
        </div>

        <div className={`hidden min-w-[100px] lg:block ${labelClass}`}>
          Budget
        </div>

        <div className={`ml-auto shrink-0 text-right ${labelClass}`}>
          Status / Responsável
        </div>
      </div>

      <div className={`flex w-[170px] shrink-0 items-center justify-end pr-3 ${labelClass}`}>
        Ações
      </div>
    </div>
  );
}

function HistoryListRow({
  record,
  isSelected,
  selectionDisabled,
  onClick,
  onExportXlsx,
  onReuse,
  onToggleSelect,
}: HistoryListRowProps) {
  const nomePromo = (record.values.nomePromo as string) || '(sem nome)';
  const nomeExibicao = (record.values.nomeExibicao as string) || '';
  const inicio = (record.values.inicioVigencia as string) || '';
  const fim = (record.values.fimVigencia as string) || '';
  const budget = (record.values.budgetValor as string) || '';
  const canExportXlsx = hasXlsxExporter(record);

  return (
    <div
      className={`
        group overflow-hidden rounded-xl border bg-white shadow-sm
        transition-all duration-150 hover:shadow-md
        ${isSelected ? 'border-blue-400 ring-2 ring-blue-500/20' : 'border-slate-200'}
      `}
      style={{ '--template-color': record.templateColor } as React.CSSProperties}
    >
      <div className="flex items-stretch">
        <div className="flex items-center pl-3">
          <label
            title={selectionDisabled ? 'Disponível apenas para o mesmo template selecionado' : 'Selecionar para exportar em lote'}
            aria-label="Selecionar briefing"
            onClick={(e) => e.stopPropagation()}
            className={`
              flex h-8 w-8 items-center justify-center rounded-lg border transition-colors
              ${isSelected
                ? 'border-blue-500 bg-blue-50'
                : selectionDisabled
                  ? 'border-slate-200 bg-slate-50 opacity-50'
                  : 'border-slate-200 bg-white hover:bg-slate-50'
              }
            `}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={selectionDisabled}
              onChange={(e) => {
                e.stopPropagation();
                onToggleSelect();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={onClick}
          className="flex min-w-0 flex-1 items-center text-left focus:outline-none"
        >
          <div className="w-1 self-stretch bg-[var(--template-color)]" />

          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4 px-4 py-3">
            <div className="flex min-w-[200px] flex-[2] items-center gap-3">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: `${record.templateColor}18`,
                  color: record.templateColor,
                }}
              >
                {TEMPLATE_ICONS[record.templateId]}
              </div>

              <div className="min-w-0">
                <h3 className="truncate text-sm font-bold text-slate-900">
                  {nomePromo}
                </h3>

                {nomeExibicao && (
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {nomeExibicao}
                  </p>
                )}
              </div>
            </div>

            <div className="hidden min-w-[140px] items-center gap-2 sm:flex">
              <span
                className="rounded-md border px-2 py-1 text-[0.65rem] font-bold"
                style={{
                  backgroundColor: `${record.templateColor}15`,
                  borderColor: `${record.templateColor}30`,
                  color: record.templateColor,
                }}
              >
                {record.templateNumber}
              </span>

              <span className="truncate text-xs text-slate-500">
                {record.templateName}
              </span>
            </div>

            <div className="hidden min-w-[160px] md:block">
              {inicio ? (
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <CalendarDays size={12} className="text-slate-400" />
                  <span>
                    {inicio.slice(0, 10)} → {fim ? fim.slice(0, 10) : '—'}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-slate-400">Sem vigência</span>
              )}
            </div>

            <div className="hidden min-w-[100px] lg:block">
              <span
                className={`
                  text-xs
                  ${budget ? 'font-semibold text-slate-800' : 'font-normal text-slate-400'}
                `}
              >
                {budget ? formatCurrency(budget) : '—'}
              </span>
            </div>

            <div className="ml-auto flex shrink-0 flex-col items-end gap-1">
              <StatusBadge status={record.status} />

              <div className="flex items-center gap-1 text-[0.68rem] text-slate-400">
                <User size={11} />
                <span>{record.author}</span>
              </div>

              <span className="text-[0.65rem] text-slate-400">
                {formatDateRelative(record.updatedAt)}
              </span>
            </div>
          </div>
        </button>

        <div className="flex shrink-0 items-center gap-1 pr-3">
          <button
            type="button"
            title="Reaproveitar"
            aria-label="Reaproveitar"
            onClick={(e) => {
              e.stopPropagation();
              onReuse();
            }}
            className="
              flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-blue-700
              transition-colors hover:bg-blue-50
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <RefreshCw size={16} />
            <span className="hidden sm:inline">Reaproveitar</span>
          </button>

          {canExportXlsx && (
            <button
              type="button"
              title="Baixar XLSX"
              aria-label="Baixar XLSX"
              onClick={(e) => {
                e.stopPropagation();
                onExportXlsx();
              }}
              className="
                flex h-8 items-center justify-center gap-1 rounded-lg px-2 text-xs font-bold text-emerald-700
                transition-colors hover:bg-emerald-50
                focus:outline-none focus:ring-2 focus:ring-emerald-500/30
              "
            >
              <FileSpreadsheet size={16} />
              Baixar
            </button>
          )}

          <button
            type="button"
            title="Visualizar"
            aria-label="Visualizar"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="
              flex h-8 w-8 items-center justify-center rounded-lg text-slate-500
              transition-colors hover:bg-slate-100 hover:text-slate-800
              focus:outline-none focus:ring-2 focus:ring-blue-500/30
            "
          >
            <Eye size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

interface PaginationProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}

function Pagination({ page, pageCount, onChange }: PaginationProps) {
  const items = getPaginationItems(page, pageCount);

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > pageCount || nextPage === page) return;
    onChange(nextPage);
  };

  const buttonBaseClass =
    'flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30';

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      <button
        type="button"
        onClick={() => goToPage(1)}
        disabled={page === 1}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Primeira página"
      >
        <ChevronsLeft size={16} />
      </button>

      <button
        type="button"
        onClick={() => goToPage(page - 1)}
        disabled={page === 1}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Página anterior"
      >
        <ChevronLeft size={16} />
      </button>

      {items.map((item, index) => {
        if (item === 'ellipsis') {
          return (
            <span
              key={`ellipsis-${index}`}
              className="flex h-9 min-w-9 items-center justify-center text-sm font-semibold text-slate-400"
            >
              ...
            </span>
          );
        }

        const isActive = item === page;

        return (
          <button
            key={item}
            type="button"
            onClick={() => goToPage(item)}
            className={
              isActive
                ? `${buttonBaseClass} border-blue-600 bg-blue-600 text-white`
                : `${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50`
            }
          >
            {item}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => goToPage(page + 1)}
        disabled={page === pageCount}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Próxima página"
      >
        <ChevronRight size={16} />
      </button>

      <button
        type="button"
        onClick={() => goToPage(pageCount)}
        disabled={page === pageCount}
        className={`${buttonBaseClass} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Última página"
      >
        <ChevronsRight size={16} />
      </button>
    </div>
  );
}

export default function HistoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const templates = useTemplates();
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<HistoryStatus | 'all'>('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [page, setPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState<HistoryRecord | null>(null);
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [autoOpenHandledId, setAutoOpenHandledId] = useState<string | null>(null);


  const selectedBriefingIdFromNavigation = useMemo(() => {
    const state = location.state as { selectedBriefingId?: string | number; openBriefing?: boolean } | null;
    const params = new URLSearchParams(location.search);
    const idFromState = state?.selectedBriefingId;
    const idFromQuery = params.get('briefingId');

    if (idFromState !== undefined && idFromState !== null && String(idFromState).trim() !== '') {
      return String(idFromState);
    }

    if (idFromQuery !== null && String(idFromQuery).trim() !== '') {
      return String(idFromQuery);
    }

    return null;
  }, [location.search, location.state]);

  const loadRecords = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await fetchBriefingPromocoes();
      setRecords(data);
    } catch (error) {
      console.log(error);
      setLoadError(error instanceof Error ? error.message : 'Não foi possível carregar o histórico de promoções.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);


  useEffect(() => {
    if (!selectedBriefingIdFromNavigation) return;
    if (loading) return;
    if (autoOpenHandledId === selectedBriefingIdFromNavigation) return;

    const openCreatedBriefing = async () => {
      try {
        setAutoOpenHandledId(selectedBriefingIdFromNavigation);
        setSearch('');
        setStatusFilter('all');
        setTemplateFilter('all');
        setAuthorFilter('all');
        setPage(1);

        const localRecord = records.find((record) => String(record.id) === selectedBriefingIdFromNavigation);
        const recordToOpen = localRecord || await fetchBriefingPromocao(selectedBriefingIdFromNavigation);

        if (recordToOpen) {
          setSelectedRecord(recordToOpen);
          setDrawerOpen(true);
        }
      } catch (error) {
        console.log(error);
      }
    };

    openCreatedBriefing();
  }, [autoOpenHandledId, loading, records, selectedBriefingIdFromNavigation]);

  const activeFilterCount = [
    statusFilter !== 'all' ? 1 : 0,
    templateFilter !== 'all' ? 1 : 0,
    authorFilter !== 'all' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const allAuthors = useMemo(
    () => Array.from(new Set(records.map((r) => r.author).filter(Boolean))).sort(),
    [records],
  );

  const allTemplates = useMemo(
    () => templates.map((template) => ({ id: template.id, name: template.name })),
    [templates],
  );

  const recordsById = useMemo(
    () => new Map(records.map((record) => [String(record.id), record])),
    [records],
  );

  const selectedRecords = useMemo(
    () => selectedRecordIds
      .map((id) => recordsById.get(id))
      .filter((record): record is HistoryRecord => Boolean(record)),
    [recordsById, selectedRecordIds],
  );

  const selectedRecordIdSet = useMemo(
    () => new Set(selectedRecordIds),
    [selectedRecordIds],
  );

  const selectedTemplateId = selectedRecords[0]?.templateId ?? null;
  const selectedTemplateName = selectedRecords[0]?.templateName ?? '';

  useEffect(() => {
    setSelectedRecordIds((prev) => {
      const next = prev.filter((id) => recordsById.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [recordsById]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();

    return records.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (templateFilter !== 'all' && r.templateId !== templateFilter) return false;
      if (authorFilter !== 'all' && r.author !== authorFilter) return false;

      if (q) {
        const nomePromo = ((r.values.nomePromo as string) ?? '').toLowerCase();
        const nomeExibicao = ((r.values.nomeExibicao as string) ?? '').toLowerCase();
        const descricao = ((r.values.descricao as string) ?? '').toLowerCase();
        const codigoCupom = ((r.values.codigoCupom as string) ?? '').toLowerCase();
        const author = r.author.toLowerCase();

        if (
          !nomePromo.includes(q) &&
          !nomeExibicao.includes(q) &&
          !descricao.includes(q) &&
          !codigoCupom.includes(q) &&
          !r.templateName.toLowerCase().includes(q) &&
          !author.includes(q) &&
          !r.id.toLowerCase().includes(q)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [records, search, statusFilter, templateFilter, authorFilter]);

  const pageSize = viewMode === 'list' ? PAGE_SIZE_LIST : PAGE_SIZE_GRID;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const stats = useMemo(
    () => ({
      total: records.length,
      ativos: records.filter((r) => r.status === 'ativo').length,
      agendados: records.filter((r) => r.status === 'agendado').length,
      rascunhos: records.filter((r) => r.status === 'rascunho').length,
    }),
    [records],
  );

  const handleFilterChange = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const handleOpenRecord = (record: HistoryRecord) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  const handleExportRecordXlsx = (record: HistoryRecord) => {
    exportRecordXlsx(record, templates);
  };

  const handleToggleRecordSelection = (record: HistoryRecord) => {
    if (!hasXlsxExporter(record)) return;

    const recordId = String(record.id);

    setSelectedRecordIds((prev) => {
      if (prev.includes(recordId)) {
        return prev.filter((id) => id !== recordId);
      }

      const firstSelectedRecord = prev
        .map((id) => recordsById.get(id))
        .find((item): item is HistoryRecord => Boolean(item));

      if (firstSelectedRecord && firstSelectedRecord.templateId !== record.templateId) {
        return prev;
      }

      return [...prev, recordId];
    });
  };

  const handleClearSelection = () => {
    setSelectedRecordIds([]);
  };

  const handleExportSelectedXlsx = () => {
    if (!selectedTemplateId || selectedRecords.length === 0) return;

    const sameTemplateRecords = selectedRecords.filter(
      (record) => record.templateId === selectedTemplateId,
    );

    exportBriefingsByTemplate(
      selectedTemplateId,
      sameTemplateRecords.map((record) => getNormalizedRecordValues(record, templates)),
    );
  };

  const handleReuseRecord = (record: HistoryRecord) => {
    navigate(CREATE_PROMOTION_ROUTE, { state: buildReuseState(record, templates) });
  };

  const handleRecordDeleted = (record: HistoryRecord) => {
    setRecords((prev) => prev.filter((item) => item.id !== record.id));
    setSelectedRecordIds((prev) => prev.filter((id) => id !== String(record.id)));
    setSelectedRecord(null);
    setDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setTemplateFilter('all');
    setAuthorFilter('all');
    setSearch('');
    setPage(1);
  };

  const isRecordSelectionDisabled = (record: HistoryRecord) => {
    const isSelected = selectedRecordIdSet.has(String(record.id));
    return !isSelected && (
      !hasXlsxExporter(record) ||
      Boolean(selectedTemplateId && record.templateId !== selectedTemplateId)
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto w-full">
        {loadError && (
          <section className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
            <button
              type="button"
              onClick={loadRecords}
              className="ml-3 font-bold underline"
            >
              Tentar novamente
            </button>
          </section>
        )}

        {loading && (
          <section className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
            Carregando histórico de promoções...
          </section>
        )}

        <section className="mb-6 flex flex-wrap gap-2">
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
            {stats.total} total
          </span>

          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
            {stats.ativos} ativos
          </span>

          <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-700 ring-1 ring-sky-200">
            {stats.agendados} agendados
          </span>

          <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
            {stats.rascunhos} rascunhos
          </span>
        </section>

        <section
          className="
            mb-6 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm
            xl:flex-row xl:items-center
          "
        >
          <div className="relative min-w-[220px] flex-1">
            <Search
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              placeholder="Buscar por nome, cupom, autor..."
              value={search}
              onChange={(e) => handleFilterChange(() => setSearch(e.target.value))}
              className="
                h-10 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-800
                outline-none transition-colors placeholder:text-slate-400
                focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
              "
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTER_OPTIONS.map((opt) => {
              const isActive = statusFilter === opt.value;

              const classes =
                opt.value === 'all'
                  ? isActive
                    ? 'border-slate-800 bg-slate-800 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  : isActive
                    ? STATUS_CHIP_CLASSES[opt.value]
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50';

              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    handleFilterChange(() =>
                      setStatusFilter(opt.value as HistoryStatus | 'all'),
                    )
                  }
                  className={`
                    h-8 rounded-full border px-3 text-xs transition-colors
                    ${isActive ? 'font-bold' : 'font-medium'}
                    ${classes}
                  `}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="hidden h-8 w-px bg-slate-200 xl:block" />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:items-center">
            <label className="relative min-w-[180px]">
              <span className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500">
                Template
              </span>

              <select
                value={templateFilter}
                onChange={(e) => handleFilterChange(() => setTemplateFilter(e.target.value))}
                className="
                  h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800
                  outline-none transition-colors
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
                "
              >
                <option value="all">Todos os templates</option>
                {allTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="relative min-w-[160px]">
              <span className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500">
                Responsável
              </span>

              <select
                value={authorFilter}
                onChange={(e) => handleFilterChange(() => setAuthorFilter(e.target.value))}
                className="
                  h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800
                  outline-none transition-colors
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
                "
              >
                <option value="all">Todos</option>
                {allAuthors.map((author) => (
                  <option key={author} value={author}>
                    {author}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="
                inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-slate-200
                bg-white px-3 text-xs font-semibold text-slate-600 transition-colors
                hover:bg-slate-50 hover:text-slate-900
              "
            >
              Limpar filtros
              <X size={13} />
            </button>
          )}

          <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 p-1 xl:ml-auto">
            <button
              type="button"
              title="Visualização em grade"
              aria-label="Visualização em grade"
              onClick={() => {
                setViewMode('grid');
                setPage(1);
              }}
              className={`
                flex h-8 w-9 items-center justify-center rounded-lg transition-colors
                ${viewMode === 'grid'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
                }
              `}
            >
              <Grid3X3 size={17} />
            </button>

            <button
              type="button"
              title="Visualização em lista"
              aria-label="Visualização em lista"
              onClick={() => {
                setViewMode('list');
                setPage(1);
              }}
              className={`
                flex h-8 w-9 items-center justify-center rounded-lg transition-colors
                ${viewMode === 'list'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
                }
              `}
            >
              <List size={18} />
            </button>
          </div>
        </section>

        <section className="mb-4 flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <div className="relative">
            <Filter size={18} className="text-slate-500" />

            {activeFilterCount > 0 && (
              <span
                className="
                  absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full
                  bg-blue-600 px-1 text-[0.6rem] font-bold leading-none text-white
                "
              >
                {activeFilterCount}
              </span>
            )}
          </div>

          <span>
            {filtered.length === records.length
              ? `${filtered.length} registros`
              : `${filtered.length} de ${records.length} registros`}
          </span>

          {filtered.length > 0 && (
            <span className="text-slate-400">
              · página {safePage} de {pageCount}
            </span>
          )}
        </section>

        {selectedRecords.length > 0 && (
          <section className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
              <FileSpreadsheet size={18} className="shrink-0 text-blue-700" />

              <span className="font-semibold text-blue-900">
                {selectedRecords.length} {selectedRecords.length === 1 ? 'selecionado' : 'selecionados'}
              </span>

              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200">
                {selectedTemplateName}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleClearSelection}
                className="
                  inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-blue-200
                  bg-white px-3 text-xs font-semibold text-blue-700 transition-colors
                  hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30
                "
              >
                Limpar seleção
                <X size={13} />
              </button>

              <button
                type="button"
                onClick={handleExportSelectedXlsx}
                className="
                  inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-3
                  text-xs font-bold text-white transition-colors hover:bg-blue-800
                  focus:outline-none focus:ring-2 focus:ring-blue-500/30
                "
              >
                <FileSpreadsheet size={15} />
                Exportar XLSX
              </button>
            </div>
          </section>
        )}

        {filtered.length === 0 ? (
          <section
            className="
              rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm
            "
          >
            <Search size={42} className="mx-auto mb-3 text-slate-300" />

            <h2 className="text-base font-semibold text-slate-600">
              Nenhum resultado encontrado
            </h2>

            <p className="mt-1 text-sm text-slate-400">
              Tente ajustar os filtros ou o termo de busca.
            </p>
          </section>
        ) : viewMode === 'grid' ? (
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {paginated.map((record) => {
              const isSelected = selectedRecordIdSet.has(String(record.id));
              const selectionDisabled = isRecordSelectionDisabled(record);

              return (
                <HistoryCard
                  key={record.id}
                  record={record}
                  isSelected={isSelected}
                  selectionDisabled={selectionDisabled}
                  onClick={() => handleOpenRecord(record)}
                  onExportXlsx={() => handleExportRecordXlsx(record)}
                  onReuse={() => handleReuseRecord(record)}
                  onToggleSelect={() => handleToggleRecordSelection(record)}
                />
              );
            })}
          </section>
        ) : (
          <section className="flex flex-col gap-3">
            <HistoryListHeader />

            {paginated.map((record) => {
              const isSelected = selectedRecordIdSet.has(String(record.id));
              const selectionDisabled = isRecordSelectionDisabled(record);

              return (
                <HistoryListRow
                  key={record.id}
                  record={record}
                  isSelected={isSelected}
                  selectionDisabled={selectionDisabled}
                  onClick={() => handleOpenRecord(record)}
                  onExportXlsx={() => handleExportRecordXlsx(record)}
                  onReuse={() => handleReuseRecord(record)}
                  onToggleSelect={() => handleToggleRecordSelection(record)}
                />
              );
            })}
          </section>
        )}

        {pageCount > 1 && (
          <div className="mt-8 flex justify-center">
            <Pagination
              page={safePage}
              pageCount={pageCount}
              onChange={(nextPage) => {
                setPage(nextPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </div>
        )}
      </main>

      <HistoryDetailDrawer
        record={selectedRecord}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDeleted={handleRecordDeleted}
        onReuse={handleReuseRecord}
      />
    </div>
  );
}
